/* eslint-disable @typescript-eslint/no-explicit-any */
import { readAccessDetails } from "@/components/unlock-request/access-details";
import {
  AccessRequestTypeEnum,
  StatusEnumAccessRequest,
} from "@/enum/access_request";
import UnlockDetail from "@/components/unlock-request/unlock-detail";
import {
  ACCESS_REQUEST_BASE_PATH,
  unlockTabPath,
} from "@/constants/access-request";
import { AssignmentsEnum } from "@/enum/assignments";
import {
  useAssignmentAbilities,
  useHasFullAccess,
} from "@/hooks/use-assignment-permission";
import { useCooldown } from "@/hooks/use-cooldown";
import { useCreateAccessMutation } from "@/services/access-automation";
import { CreateAccessRequest } from "@/services/dtos/accessAutomation.dto";
import { UserResponsibleTypeEnum } from "@/services/dtos/enums/accessAutomation.enum";
import {
  useGetByIdQuery
} from "@/services/request-access";
import { formatNumericStringToCPF } from "@/utils/format-cps";
import { validateOperatorPassword } from "@/utils/password-generator";
import { validateAccessRequest } from "@/utils/validateAccessRequest";
import { zodResolver } from "@hookform/resolvers/zod";
import { Load } from "components/load";
import PageError from "components/page-error";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { toast } from "components/ui/use-toast";
import { ChevronLeft } from "lucide-react";
import React, { useRef } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { CardContract } from "./components/card-contract";
import { CompletedConfig } from "./components/completed-config";
import {
  Config,
  ConfigRef,
  PasswordConfigurationTypeEnum,
} from "./components/config";
import { DevolutivaForm } from "@/components/access-request/form";
import { HistoricoTimeline } from "@/components/access-request/timeline";
import { UserCard } from "@/components/access-request/user-card";
import {
  FormData,
  FormSchema,
} from "@/components/access-request/validate-form";

export const description =
  "A product edit page. The product edit page has a form to edit the product details, stock, product category, product status, and product images. The product edit page has a sidebar navigation and a main content area. The main content area has a form to edit the product details, stock, product category, product status, and product images. The sidebar navigation has as to product details, stock, product category, product status, and product images.";

export const iframeHeight = "1100px";

export const containerClassName = "w-full h-full";

export const EditRequest: React.FC = () => {
  const configRef = useRef<ConfigRef>(null);
  const cooldown = useCooldown(6);
  const navigate = useNavigate();
  const formMethods = useForm<FormData>({
    resolver: zodResolver(FormSchema),
  });
  const { id_request } = useParams();
  const { canUpdate } = useAssignmentAbilities(AssignmentsEnum.ACCESS_REQUEST);
  const hasFullAccess: boolean = useHasFullAccess();
  const canDecideUnlock: boolean = hasFullAccess || canUpdate;

  const { data, isLoading, error } = useGetByIdQuery(
    id_request?.toString() || ""
  );

  const [createAccess, { isLoading: isLoadingCreateAccess }] =
    useCreateAccessMutation();

  const handleCreateAccess = async () => {
    // Previne múltiplos cliques enquanto está processando
    if (isLoadingCreateAccess) {
      console.warn("Criação já em andamento");
      return;
    }

    try {
      console.debug(
        "[handleFinishRequest] Iniciando processo de criação de operador"
      );
      if (!data) {
        console.error("Dados da requisição não disponíveis");
        return;
      }

      // 1. Validar dados básicos
      const configData = configRef.current?.getConfigData();
      console.debug("Dados de configuração:", configData);
      if (!configData) return;

      // 2. Validar dados
      const isAccessRequestDataValid = validateAccessRequest(data);
      if (!isAccessRequestDataValid) {
        throw new Error("Dados inválidos para criação de usuário");
      }

      const userPasswordValidationResult = validateOperatorPassword(
        configData.userPassword.value
      );
      const operatorPasswordValidationResult = validateOperatorPassword(
        configData.operatorPassword.value
      );

      if (
        configData.userPassword.type == PasswordConfigurationTypeEnum.MANUAL &&
        !userPasswordValidationResult.isValid
      ) {
        configData.setUserPasswordError(
          userPasswordValidationResult.errors || []
        );
        throw new Error(
          "Senha inválida: " +
          (userPasswordValidationResult.errors?.join(", ") ||
            "Erro desconhecido")
        );
      }

      if (
        configData.operatorPassword.type ==
        PasswordConfigurationTypeEnum.MANUAL &&
        !operatorPasswordValidationResult.isValid
      ) {
        configData.setOperatorPasswordError(
          operatorPasswordValidationResult.errors || []
        );
        throw new Error(
          "Senha inválida: " +
          (operatorPasswordValidationResult.errors?.join(", ") ||
            "Erro desconhecido")
        );
      }

      configData.setUserPasswordError([""]);
      configData.setOperatorPasswordError([""]);

      const formattedCpf = formatNumericStringToCPF(data.cpf);

      // 3. Montar body com objetos separados de create e update
      const createAccessRequestBody: CreateAccessRequest = {

        body: {
          create: {
            cpf: formattedCpf,
            storeCode: Number(data.store),
            userResponsibleType:
              configData.userClassValue as UserResponsibleTypeEnum,
            userPassword: configData.userPassword.value,
            operatorPassword: configData.operatorPassword.value,
          },
          update: {
            id_prisma: id_request?.toString() || "",
            comment: "Requisição de acesso Finalizada.",
            status: StatusEnumAccessRequest.COMPLETED,
          },
        },
      };

      console.log(createAccessRequestBody, "body");
      console.debug("createAccessRequestBody", createAccessRequestBody);

      // Simulando erro para testar cooldown do botão
      // throw { status: 500, data: { messages: ["[MOCK] Erro simulado para teste de cooldown"] } };

      // 4. Uma única chamada que faz tudo: criar + atualizar + histórico
      const result = await createAccess(createAccessRequestBody).unwrap();

      console.debug("Resposta da criação:", result);

      toast({ variant: "success", description: "Usuário criado com sucesso." });
      navigate("/financeiro/requisicao-de-acesso");
    } catch (error: any) {
      let errorMessage = "Erro ao criar usuário.";
      console.error("[Erro] Detalhes do erro:", error);

      if (typeof error === "object" && error !== null) {
        if ("status" in error) {
          console.error(
            "[Erro] Status do erro:",
            (error as { status: string | number }).status
          );
          cooldown.start();
        }
        if ("data" in error) {
          const errorData = (error as { data: any }).data;
          console.error("[Erro] Dados da resposta de erro:", errorData);
          errorMessage = errorData?.messages?.[0] || errorMessage;
        }
      }

      toast({
        variant: "error",
        description: errorMessage,
      });
    }
  };

  const isClosed = data?.history.some(
    (event: { status: string }) =>
      event.status === StatusEnumAccessRequest.COMPLETED
  );

  const isRejected = data?.history.some(
    (event: { status: string }) =>
      event.status === StatusEnumAccessRequest.REJECTED
  );

  if (isLoading) return <Load />;
  if (error)
    return (
      <PageError
        status={500}
        message="Ocorreu um erro interno. Por favor, tente novamente mais tarde."
      />
    );

  if (data?.type === AccessRequestTypeEnum.UNLOCK) {
    return (
      <UnlockDetail
        request={data}
        backPath={unlockTabPath(ACCESS_REQUEST_BASE_PATH.FINANCE)}
        canDecide={canDecideUnlock}
      />
    );
  }

  return (
    <>
      {isLoadingCreateAccess && (
        <div className="w-full h-screen bg-white fixed top-0 left-0 z-50 pointer-events-auto">
          <Load />
        </div>
      )}
      <div className="flex flex-col min-h-screen w-full">
        <div className="flex min-h-screen w-full flex-col bg-muted/40">
          <div className="flex flex-col sm:gap-4 sm:py-4">
            <main className="grid flex-1 items-start gap-4 p-4 sm:px-6 sm:py-0 md:gap-8">
              <div className="mx-auto grid w-full flex-1 auto-rows-max gap-4">
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => navigate("/financeiro/requisicao-de-acesso/")}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="sr-only">Voltar</span>
                  </Button>
                  <h1 className="flex-1 shrink-0 whitespace-nowrap text-xl font-semibold tracking-tight sm:grow-0">
                    Solicitação de Acesso
                  </h1>
                  <Badge variant="outline" className="ml-auto sm:ml-0">
                    Loja {data?.store}
                  </Badge>
                  <div className="hidden items-center gap-2 md:ml-auto md:flex">
                    {!isClosed && (
                      <Button size="sm" onClick={handleCreateAccess} disabled={cooldown.isActive}>
                        {cooldown.isActive ? `Aguarde... ${cooldown.remaining}s` : "Criar Usuário"}
                      </Button>
                    )}
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-[1fr_250px] lg:grid-cols-3 lg:gap-8">
                  <div className="grid auto-rows-max items-start gap-4 lg:col-span-2 lg:gap-8">
                    <UserCard
                      formMethods={formMethods}
                      cpf={data?.cpf ?? ""}
                    />
                    {isClosed ? (
                      <CompletedConfig accessDetails={readAccessDetails(data?.access_details)} />
                    ) : (
                      <>
                        <Config ref={configRef} />
                        <DevolutivaForm />
                      </>
                    )}
                  </div>
                  <div className="grid auto-rows-max items-start gap-4 lg:gap-8">
                    <HistoricoTimeline event={(data as any)?.history} />
                    <CardContract
                      accessRequestCompleted={isClosed || isRejected}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-center gap-2 md:hidden">
                  <Button variant="outline" size="sm">
                    Discard
                  </Button>
                  <Button size="sm">Save Product</Button>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </>
  );
};
